import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

/**
 * Global modal that surfaces API failures from the data handler on any page.
 * The data handler dispatches a window "apiError" event on a failed request;
 * this component listens for it and shows a single consistent popup.
 */
const ApiErrorModal = () => {
    const [open, setOpen] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [details, setDetails] = useState(null);

    useEffect(() => {
        const handleApiError = (event) => {
            setIsBusy(Boolean(event.detail?.isBusy));
            setDetails(event.detail || null);
            setOpen(true);
        };
        window.addEventListener("apiError", handleApiError);
        return () => window.removeEventListener("apiError", handleApiError);
    }, []);

    const handleClose = () => setOpen(false);
    const handleReload = () => window.location.reload();

    // Compact technical reference so a user's screenshot gives a dev enough to triage.
    const detailParts = details
        ? [
              details.action && `action: ${details.action}`,
              details.status && `status: ${details.status}`,
              details.code && `code: ${details.code}`,
              details.time && `time: ${details.time}`,
          ].filter(Boolean)
        : [];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>{isBusy ? "System busy" : "Something went wrong"}</DialogTitle>
            <DialogContent>
                <Typography sx={{ color: "#374151", fontSize: "0.95rem" }}>
                    {isBusy
                        ? "The report couldn't load because the system is busy with many users right now — please try again."
                        : "Couldn't load the data — please try again."}
                </Typography>
                {detailParts.length > 0 && (
                    <Typography
                        sx={{
                            mt: 1.5,
                            color: "#9ca3af",
                            fontSize: "0.7rem",
                            fontFamily: "monospace",
                            wordBreak: "break-word",
                        }}
                    >
                        {`Technical details: ${detailParts.join(" · ")}`}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
                {isBusy && (
                    <Button variant="contained" onClick={handleReload}>
                        Reload
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ApiErrorModal;
